import { useEffect } from 'react';
import { useTranslation } from '../components/I18nContext';
import { Language } from '../data/locales';

export interface SeoMetaEntry {
  title: string;
  description: string;
}

export interface SeoMetaOptions {
  /** Per-language title + description. Falls back to 'de' if current language key is missing. */
  meta: Record<Language, SeoMetaEntry>;
  /** Path used for canonical + OG URL, e.g. '/services/ai'. Defaults to '/'. */
  canonicalPath?: string;
  /** Absolute URL for og:image. Defaults to the HenselX logo. */
  ogImage?: string;
}

const BASE_URL = 'https://henselx.it';
const DEFAULT_OG_IMAGE = `${BASE_URL}/Grafx/henselx_web_primary.png`;
const DEFAULT_TITLE = 'HenselX | Sovereign IT & AI Architecture — Acri, Italy';

function setOrCreateMeta(selector: string, attrName: string, attrValue: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrCreateLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export const useSeoMeta = ({ meta, canonicalPath = '/', ogImage }: SeoMetaOptions) => {
  const { language } = useTranslation();

  useEffect(() => {
    const entry = meta[language] ?? meta['de'];
    const { title, description } = entry;
    const image = ogImage ?? DEFAULT_OG_IMAGE;
    const canonicalUrl = `${BASE_URL}${canonicalPath}`;

    document.title = title;

    setOrCreateMeta('meta[name="description"]', 'name', 'description', description);

    setOrCreateMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setOrCreateMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setOrCreateMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setOrCreateMeta('meta[property="og:image"]', 'property', 'og:image', image);
    setOrCreateMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setOrCreateMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'HenselX');

    setOrCreateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setOrCreateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setOrCreateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setOrCreateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);

    setOrCreateLink('canonical', canonicalUrl);

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [language, meta, canonicalPath, ogImage]);
};
